import React from "react";
import { Volume2, MessageSquare, ExternalLink } from "lucide-react";

export default function AvailabilityMatrix({ availability, platforms, countries, languages }) {
  const lang = (code) => languages.find((l) => l.code === code)?.name || code.toUpperCase();
  const platform = (id) => platforms.find((p) => p.id === id);
  const country = (code) => countries.find((c) => c.code === code);

  return (
    <div className="border border-white/10 rounded-md overflow-hidden bg-obsidian-surface/60" data-testid="availability-matrix">
      <div className="grid grid-cols-[140px_120px_1fr_1fr_120px_60px] text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-heading border-b border-white/10 bg-black/30">
        <div className="px-4 py-3">Platform</div>
        <div className="px-4 py-3 border-l border-white/5">Country</div>
        <div className="px-4 py-3 border-l border-white/5">Audio</div>
        <div className="px-4 py-3 border-l border-white/5">Subtitles</div>
        <div className="px-4 py-3 border-l border-white/5">Version</div>
        <div className="px-4 py-3 border-l border-white/5">Open</div>
      </div>
      <div>
        {availability.map((a, idx) => {
          const p = platform(a.platform);
          const c = country(a.country);
          return (
            <div
              key={idx}
              className="grid grid-cols-[140px_120px_1fr_1fr_120px_60px] text-sm border-b border-white/5 hover:bg-white/[0.02] transition"
              data-testid={`matrix-row-${idx}`}
            >
              <div className="px-4 py-4 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: p?.color || "#fff" }} />
                <span className="text-white font-heading">{p?.name || a.platform}</span>
              </div>
              <div className="px-4 py-4 border-l border-white/5 text-zinc-300 font-heading flex items-center gap-2">
                <span>{c?.flag}</span>
                <span>{c?.code || a.country}</span>
              </div>
              <div className="px-4 py-4 border-l border-white/5 flex flex-wrap gap-1.5">
                {a.audio.map((code) => (
                  <span
                    key={code}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${
                      code === "tr"
                        ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
                        : "bg-white/5 border-white/10 text-zinc-300"
                    } font-heading`}
                    data-testid={`audio-chip-${a.platform}-${a.country}-${code}`}
                  >
                    <Volume2 className="h-3 w-3" /> {lang(code)}
                  </span>
                ))}
              </div>
              <div className="px-4 py-4 border-l border-white/5 flex flex-wrap gap-1.5">
                {a.subtitles.map((code) => (
                  <span
                    key={code}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-white/5 border border-white/10 text-zinc-300 font-heading"
                  >
                    <MessageSquare className="h-3 w-3" /> {lang(code)}
                  </span>
                ))}
              </div>
              <div className="px-4 py-4 border-l border-white/5 text-xs text-zinc-400 font-heading flex flex-col">
                <span className="text-zinc-200">{a.version}</span>
                <span className="text-zinc-500">{a.quality}</span>
              </div>
              <div className="px-4 py-4 border-l border-white/5 flex items-center justify-center">
                {a.affiliate_url || a.web_url ? (
                  <a
                    href={a.affiliate_url || a.web_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber hover:text-amber-hover transition"
                    title="Open with affiliate link"
                    data-testid={`open-link-${idx}`}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                ) : (
                  <span className="text-zinc-700">—</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
